<template>
  <transition name="filter-positions-readiness-modal" appear>
    <div
      v-if="value"
      class="filter-positions-readiness-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-positions-readiness-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-positions-readiness-modal__card">
        <div class="filter-positions-readiness-modal__header">
          <span id="filter-positions-readiness-modal-title" class="filter-positions-readiness-modal__title">
            Filter positions
          </span>
          <button
            type="button"
            class="filter-positions-readiness-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-positions-readiness-modal__filters-row">
          <div class="filter-positions-readiness-modal__column">
            <RadioInput
              label="Vacancy"
              :availableItems="vacancyOptions"
              :checkedItem="vacancyLabel"
              :uncheckedValue="vacancyClearKey"
              @changeradio="onVacancyChange"
            />
          </div>

          <div class="filter-positions-readiness-modal__column">
            <RadioInput
              label="Excluded from Average"
              :availableItems="excludedOptions"
              :checkedItem="excludedLabel"
              :uncheckedValue="excludedClearKey"
              @changeradio="onExcludedChange"
            />
          </div>
        </div>

        <div class="filter-positions-readiness-modal__divider" />

        <div class="filter-positions-readiness-modal__department">
          <span class="filter-positions-readiness-modal__column-title">Department</span>

          <DepartmentPickerField
            browse-list
            browse-list-requires-query
            :value="department"
            :departments="departments"
            :loading="loading"
            @input="setDepartment"
            @search="onSearch"
            @clear="onClear"
          />

          <p v-if="!department" class="filter-positions-readiness-modal__hint">
            Leave empty to show positions across the whole organization.
          </p>

          <template v-else>
            <div
              class="filter-positions-readiness-modal__scope-toggle"
              role="radiogroup"
              aria-label="Department scope"
            >
              <button
                type="button"
                class="filter-positions-readiness-modal__scope-option"
                :class="{ 'filter-positions-readiness-modal__scope-option--active': scope === 'direct' }"
                role="radio"
                :aria-checked="scope === 'direct'"
                @click="setScope('direct')"
              >
                This department only
              </button>
              <span class="filter-positions-readiness-modal__scope-divider" aria-hidden="true" />
              <button
                type="button"
                class="filter-positions-readiness-modal__scope-option"
                :class="{ 'filter-positions-readiness-modal__scope-option--active': scope === 'subtree' }"
                role="radio"
                :aria-checked="scope === 'subtree'"
                @click="setScope('subtree')"
              >
                Include all sub-departments
              </button>
            </div>
            <p class="filter-positions-readiness-modal__hint">
              {{ scopeHint }}
            </p>
          </template>
        </div>

        <div class="filter-positions-readiness-modal__divider" />

        <div class="filter-positions-readiness-modal__footer">
          <button
            type="button"
            class="filter-positions-readiness-modal__reset"
            @click="handleReset"
          >
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DepartmentPickerField from "@components/DepartmentPickerField/DepartmentPickerField.vue";
import RadioInput from "@components/Radio/RadioInput.vue";

const props = defineProps({
  value: { type: Boolean, default: false },
  initialVacancy: {
    type: String,
    default: null,
    validator: (value) => [null, "vacant", "not_vacant"].includes(value),
  },
  initialExcluded: {
    type: String,
    default: null,
    validator: (value) => [null, "excluded", "not_excluded"].includes(value),
  },
  initialDepartment: { type: Object, default: null },
  initialScope: {
    type: String,
    default: "subtree",
    validator: (value) => ["direct", "subtree"].includes(value),
  },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const DEFAULT_SCOPE = "subtree";

const emit = defineEmits(["input", "close", "apply", "reset", "search", "clear"]);

const vacancyOptions = [
  { label: "No Filter" },
  { label: "Vacant" },
  { label: "Not Vacant" },
];
const excludedOptions = [
  { label: "No Filter" },
  { label: "Excluded" },
  { label: "Not Excluded" },
];

const LABEL_TO_VALUE = {
  Vacant: "vacant",
  "Not Vacant": "not_vacant",
  Excluded: "excluded",
  "Not Excluded": "not_excluded",
};
const VALUE_TO_LABEL = {
  vacant: "Vacant",
  not_vacant: "Not Vacant",
  excluded: "Excluded",
  not_excluded: "Not Excluded",
};

const vacancy = ref(props.initialVacancy);
const excluded = ref(props.initialExcluded);
const department = ref(props.initialDepartment);
const scope = ref(props.initialScope);

const vacancyClearKey = ref("");
const excludedClearKey = ref("");

const vacancyLabel = computed(() => VALUE_TO_LABEL[vacancy.value] || "No Filter");
const excludedLabel = computed(() => VALUE_TO_LABEL[excluded.value] || "No Filter");

const scopeHint = computed(() =>
  scope.value === "subtree"
    ? "Showing this department plus every department nested beneath it, at any depth."
    : "Showing positions belonging directly to this department only."
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      syncFromProps();
      emit("search", "");
    }
  },
  { immediate: true }
);

function syncFromProps() {
  vacancy.value = props.initialVacancy;
  excluded.value = props.initialExcluded;
  department.value = props.initialDepartment;
  scope.value = props.initialScope;
}

function onVacancyChange(payload) {
  vacancy.value = payload?.value ? LABEL_TO_VALUE[payload.value] || null : null;
}

function onExcludedChange(payload) {
  excluded.value = payload?.value ? LABEL_TO_VALUE[payload.value] || null : null;
}

function setDepartment(dept) {
  department.value = dept;
  if (!dept) scope.value = DEFAULT_SCOPE;
}

function setScope(val) {
  scope.value = val;
}

function onSearch(query) {
  emit("search", query);
}

function onClear() {
  emit("clear");
  emit("search", "");
}

function handleReset() {
  vacancy.value = null;
  excluded.value = null;
  department.value = null;
  scope.value = DEFAULT_SCOPE;
  vacancyClearKey.value = `reset-${Date.now()}`;
  excludedClearKey.value = `reset-${Date.now()}`;
  emit("reset");
  emit("search", "");
}

function handleApply() {
  emit("apply", {
    vacancy: vacancy.value,
    excluded: excluded.value,
    department: department.value,
    scope: department.value ? scope.value : null,
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-positions-readiness-modal-enter-active,
.filter-positions-readiness-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-positions-readiness-modal-enter-from,
.filter-positions-readiness-modal-leave-to {
  opacity: 0;
}

.filter-positions-readiness-modal-enter-active .filter-positions-readiness-modal__card,
.filter-positions-readiness-modal-leave-active .filter-positions-readiness-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-positions-readiness-modal-enter-from .filter-positions-readiness-modal__card,
.filter-positions-readiness-modal-leave-to .filter-positions-readiness-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-positions-readiness-modal-enter-active,
  .filter-positions-readiness-modal-leave-active,
  .filter-positions-readiness-modal-enter-active .filter-positions-readiness-modal__card,
  .filter-positions-readiness-modal-leave-active .filter-positions-readiness-modal__card {
    transition: none !important; /* honour reduced-motion over the transition rules above */
  }
}

.filter-positions-readiness-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-positions-readiness-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-positions-readiness-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-positions-readiness-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-positions-readiness-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-positions-readiness-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-positions-readiness-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-positions-readiness-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-positions-readiness-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-positions-readiness-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-positions-readiness-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-positions-readiness-modal__department {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.filter-positions-readiness-modal__hint {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.filter-positions-readiness-modal__scope-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
}

.filter-positions-readiness-modal__scope-option {
  flex: 1 1 0;
  min-width: 0;
  appearance: none;
  border: 0;
  background: #fff;
  padding: 10px 16px;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  text-align: center;
}

.filter-positions-readiness-modal__scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.filter-positions-readiness-modal__scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.filter-positions-readiness-modal__scope-divider {
  width: 1px;
  align-self: stretch;
  background: #d0d5dd;
}

.filter-positions-readiness-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-positions-readiness-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-positions-readiness-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-positions-readiness-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
